import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  Box, Typography, Button, Grid, Card, CardContent, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, FormControl, InputLabel, Alert, LinearProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Visibility as ViewIcon, Assignment as AssignmentIcon,
  CheckCircle as CheckIcon, Schedule as ScheduleIcon, Cancel as CancelIcon
} from '@mui/icons-material';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success', icon: <CheckIcon fontSize="small" /> },
  due: { label: 'Overdue', color: 'error', icon: <ScheduleIcon fontSize="small" /> },
  completed: { label: 'Completed', color: 'info', icon: <CheckIcon fontSize="small" /> },
  cancelled: { label: 'Cancelled', color: 'default', icon: <CancelIcon fontSize="small" /> }
};

const TOPICS_BY_GRADE = {
  8: ['Integers','Exponents','Common Fractions','Decimal Fractions','Percentages','Ratio and Rate','Algebraic Expressions','Algebraic Equations','Numeric Patterns','Geometry of 2D Shapes','Geometry of 3D Objects','Theorem of Pythagoras','Area and Perimeter','Surface Area and Volume','Data Handling','Probability'],
  9: ['Whole Numbers','Integers','Exponents','Common Fractions','Decimal Fractions','Percentages','Financial Mathematics','Algebraic Expressions','Algebraic Equations','Numeric Patterns','Geometric Patterns','Functions and Relationships','Graphs','Construction of Geometric Figures','Geometry of 2D Shapes','Geometry of Straight Lines','Geometry of 3D Objects','Transformation Geometry','Theorem of Pythagoras','Area and Perimeter','Surface Area and Volume','Data Handling','Probability'],
  10: ['Algebra','Number Patterns','Functions','Finance','Trigonometry','Euclidean Geometry','Analytical Geometry','Statistics','Probability'],
  11: ['Algebra','Number Patterns','Functions','Finance','Trigonometry','Euclidean Geometry','Analytical Geometry','Statistics','Probability'],
  12: ['Algebra','Patterns and Sequences','Functions','Finance','Trigonometry','Euclidean Geometry','Analytical Geometry','Statistics','Probability','Calculus']
};

export default function AssignmentsTab({ schoolId }) {
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    classId: '', title: '', description: '', grade: '', topic: '', subtopic: '', totalQuestions: 10, dueDate: ''
  });
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await api.get(`/api/schools/${schoolId}/assignments`, { params });
      const data = res.data.data || res.data;
      setAssignments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [schoolId, statusFilter]);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get(`/api/schools/${schoolId}/classes`);
      const data = res.data.data || res.data;
      setClasses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load classes:', err);
    }
  }, [schoolId]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);
  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm({ classId: '', title: '', description: '', grade: '', topic: '', subtopic: '', totalQuestions: 10, dueDate: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (a) => {
    setEditing(a);
    setForm({
      classId: a.class?._id || a.class, title: a.title, description: a.description || '',
      grade: a.grade, topic: a.topic, subtopic: a.subtopic || '',
      totalQuestions: a.totalQuestions,
      dueDate: a.dueDate ? new Date(a.dueDate).toISOString().slice(0, 10) : ''
    });
    setDialogOpen(true);
  };

  const handleClassChange = (classId) => {
    const cls = classes.find(c => c._id === classId);
    setForm(f => ({ ...f, classId, grade: cls?.grade || f.grade, topic: '', subtopic: '' }));
  };

  const handleSave = async () => {
    const missing = [];
    if (!form.classId) missing.push('Class');
    if (!form.title) missing.push('Title');
    if (!form.grade) missing.push('Grade');
    if (!form.topic) missing.push('Topic');
    if (!form.dueDate) missing.push('Due date');
    if (missing.length > 0) {
      setError('Missing required fields: ' + missing.join(', ')); return;
    }
    try {
      setSaving(true);
      if (editing) {
        await api.patch(`/api/schools/${schoolId}/assignments/${editing._id}`, {
          title: form.title, description: form.description,
          totalQuestions: form.totalQuestions, dueDate: form.dueDate
        });
        setSuccess('Assignment updated');
      } else {
        await api.post(`/api/schools/${schoolId}/assignments`, form);
        setSuccess('Assignment created');
      }
      setDialogOpen(false);
      fetchAssignments();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/schools/${schoolId}/assignments/${deleteTarget._id}`);
      setSuccess('Assignment deleted');
      setDeleteTarget(null);
      fetchAssignments();
    } catch (err) {
      setError('Failed to delete assignment');
    }
  };

  const handleViewDetail = async (assignmentId) => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const res = await api.get(`/api/schools/${schoolId}/assignments/${assignmentId}`);
      const data = res.data.data || res.data;
      setDetail(data);
    } catch (err) {
      setError('Failed to load assignment detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMarkCompleted = async (a) => {
    try {
      await api.patch(`/api/schools/${schoolId}/assignments/${a._id}`, { status: 'completed' });
      setSuccess('Assignment marked as completed');
      fetchAssignments();
    } catch (err) {
      setError('Failed to update assignment');
    }
  };

  const availableTopics = form.grade ? (TOPICS_BY_GRADE[form.grade] || []) : [];
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const daysUntilDue = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24)); };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6">Assignments</Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="due">Overdue</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>New Assignment</Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : assignments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No Assignments</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first assignment to set topic-specific tasks for your classes.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenCreate}>Create Assignment</Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {assignments.map(a => {
            const days = daysUntilDue(a.dueDate);
            const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.active;
            const progress = a.learnerCount > 0 ? Math.round((a.completedCount / a.learnerCount) * 100) : 0;
            return (
              <Grid item xs={12} md={6} key={a._id}>
                <Card sx={{ height: '100%', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>{a.title}</Typography>
                      <Chip label={sc.label} color={sc.color} size="small" icon={sc.icon} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {a.class?.name || 'Unknown class'} • Grade {a.grade} • {a.topic}{a.subtopic ? ` — ${a.subtopic}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {a.totalQuestions} questions • Due {formatDate(a.dueDate)}
                      {days !== null && a.status === 'active' && (
                        <span style={{ color: days <= 2 ? '#d32f2f' : days <= 7 ? '#ed6c02' : 'inherit' }}>
                          {' '}({days > 0 ? `${days} days left` : days === 0 ? 'Due today' : `${Math.abs(days)} days overdue`})
                        </span>
                      )}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Completion: {a.completedCount || 0}/{a.learnerCount || 0} learners</Typography>
                        <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                      <Tooltip title="View detail"><IconButton size="small" onClick={() => handleViewDetail(a._id)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      {a.status === 'active' && (
                        <Tooltip title="Mark completed"><IconButton size="small" color="success" onClick={() => handleMarkCompleted(a)}><CheckIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(a)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Assignment' : 'Create Assignment'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {!editing && (
            <FormControl fullWidth required>
              <InputLabel>Class</InputLabel>
              <Select value={form.classId} onChange={e => handleClassChange(e.target.value)} label="Class">
                {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.name} (Grade {c.grade})</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TextField label="Title" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
          <TextField label="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
          {!editing && (
            <>
              <FormControl fullWidth required>
                <InputLabel>Grade</InputLabel>
                <Select value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value, topic: '', subtopic: '' }))} label="Grade">
                  {[8,9,10,11,12].map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth required disabled={!form.grade}>
                <InputLabel>Topic</InputLabel>
                <Select value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value, subtopic: '' }))} label="Topic">
                  {availableTopics.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Subtopic (optional)" value={form.subtopic} onChange={e => setForm(f => ({ ...f, subtopic: e.target.value }))} fullWidth />
            </>
          )}
          <TextField label="Number of questions" type="number" required value={form.totalQuestions}
            onChange={e => setForm(f => ({ ...f, totalQuestions: parseInt(e.target.value) || 10 }))}
            inputProps={{ min: 1, max: 100 }} fullWidth />
          <TextField label="Due date" type="date" required value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setDetail(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Assignment Detail</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : detail ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>{detail.title}</Typography>
              {detail.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{detail.description}</Typography>}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`Grade ${detail.grade}`} size="small" />
                <Chip label={detail.topic} size="small" color="primary" variant="outlined" />
                {detail.subtopic && <Chip label={detail.subtopic} size="small" variant="outlined" />}
                <Chip label={`${detail.totalQuestions} questions`} size="small" />
                <Chip label={`Due ${formatDate(detail.dueDate)}`} size="small" />
                <Chip label={STATUS_CONFIG[detail.status]?.label || detail.status}
                  color={STATUS_CONFIG[detail.status]?.color || 'default'} size="small" />
              </Box>
              {detail.summary && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h5">{detail.summary.completed}/{detail.summary.totalLearners}</Typography>
                      <Typography variant="caption" color="text.secondary">Completed</Typography>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h5">{detail.summary.averageAccuracy}%</Typography>
                      <Typography variant="caption" color="text.secondary">Avg Accuracy</Typography>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h5">
                        {detail.summary.totalLearners > 0 ? Math.round((detail.summary.completed / detail.summary.totalLearners) * 100) : 0}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Completion Rate</Typography>
                    </CardContent></Card>
                  </Grid>
                </Grid>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Learner Progress</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Learner</TableCell>
                      <TableCell align="center">Answered</TableCell>
                      <TableCell align="center">Correct</TableCell>
                      <TableCell align="center">Accuracy</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.learnerProgress || []).map(l => (
                      <TableRow key={l._id}>
                        <TableCell>{l.name || l.username}</TableCell>
                        <TableCell align="center">{l.answered}/{detail.totalQuestions}</TableCell>
                        <TableCell align="center">{l.correct}</TableCell>
                        <TableCell align="center">{l.accuracy}%</TableCell>
                        <TableCell align="center">
                          <Chip label={l.completed ? 'Done' : 'In progress'} size="small" color={l.completed ? 'success' : 'warning'} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!detail.learnerProgress || detail.learnerProgress.length === 0) && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No learners in this class</Typography>
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailOpen(false); setDetail(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete &quot;{deleteTarget?.title}&quot;? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
